// src/pages/AdminSellerProducts.jsx
// 商家商品管理頁面 - 查看和管理特定商家的商品

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Eye,
  Star,
  ArrowLeft,
  AlertTriangle
} from 'lucide-react';

const AdminSellerProducts = () => {
  const navigate = useNavigate();
  const { sellerId } = useParams();
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);  // 動態分類列表
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadSellerAndProducts();
  }, [sellerId, categoryFilter]);

  const loadSellerAndProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // 載入商家資訊
      const sellerUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${sellerId}`;
      console.log('Loading seller:', sellerUrl);
      
      const sellerResponse = await fetch(sellerUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // 檢查 HTTP 狀態
      if (!sellerResponse.ok) {
        const errorText = await sellerResponse.text();
        console.error('Seller response error:', errorText);
        throw new Error(`HTTP ${sellerResponse.status}: ${errorText}`);
      }
      
      const sellerResult = await sellerResponse.json();
      
      if (sellerResult.error) throw new Error(sellerResult.message);
      setSeller(sellerResult.data);

      // 載入分類列表
      const categoriesUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${sellerId}/categories`;
      console.log('Loading categories:', categoriesUrl);
      
      const categoriesResponse = await fetch(categoriesUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (categoriesResponse.ok) {
        const categoriesResult = await categoriesResponse.json();
        if (!categoriesResult.error && categoriesResult.data) {
          setCategories(categoriesResult.data);
          console.log('Categories loaded:', categoriesResult.data);
        }
      }

      // 載入商品列表
      const productsUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${sellerId}/products`;
      console.log('Loading products:', productsUrl);
      
      const url = new URL(productsUrl);
      if (categoryFilter) {
        url.searchParams.append('category', categoryFilter);
      }

      const productsResponse = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // 檢查 HTTP 狀態
      if (!productsResponse.ok) {
        const errorText = await productsResponse.text();
        console.error('Products response error:', errorText);
        throw new Error(`HTTP ${productsResponse.status}: ${errorText}`);
      }
      
      const productsResult = await productsResponse.json();
      
      if (productsResult.error) throw new Error(productsResult.message);
      setProducts(productsResult.data || []);

    } catch (err) {
      console.error('載入失敗:', err);
      alert('載入失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const deleteUrl = `${process.env.REACT_APP_API_BASE_URL}/products/${productId}`;
      console.log('Deleting product:', deleteUrl);
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // 檢查 HTTP 狀態
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      loadSellerAndProducts();
      setDeleteConfirm(null);
      alert('商品刪除成功！');
    } catch (err) {
      alert('刪除失敗: ' + err.message);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {seller?.name} - 商品管理
                </h1>
                <p className="mt-1 text-gray-600">
                  {seller?.location} | 評分: {seller?.rating} | 
                  共 {seller?.stats?.total_products || 0} 個商品
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/admin/products/new?seller=${sellerId}`)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              <Plus size={18} />
              新增商品
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 搜尋和篩選 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 搜尋框 */}
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜尋商品名稱或分類..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* 分類篩選 */}
            <div className="relative">
              <Filter size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
              >
                <option value="">所有分類</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 商品列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredProducts.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">沒有找到商品</p>
              <p className="text-sm mb-6">
                {searchTerm || categoryFilter
                  ? '請嘗試其他搜尋條件'
                  : '此商家還沒有商品'}
              </p>
              {!searchTerm && !categoryFilter && (
                <button
                  onClick={() => navigate(`/admin/products/new?seller=${sellerId}`)}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  新增第一個商品
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">商品</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">分類</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">價格</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">庫存</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">評分</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">銷量</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-700">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProducts.map(product => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-4">
                          {product.images && product.images[0] ? (
                            <img
                              src={product.images[0].url}
                              alt={product.name}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                              <Package size={24} className="text-gray-400" />
                            </div>
                          )}
                          <div className="max-w-xs">
                            <p className="font-medium text-gray-900 line-clamp-1">
                              {product.name}
                            </p>
                            <p className="text-sm text-gray-500 line-clamp-1">
                              {product.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                          {product.category}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-semibold text-orange-600">
                            TWD {product.price}
                          </p>
                          {product.original_price && (
                            <p className="text-xs text-gray-400 line-through">
                              TWD {product.original_price}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`font-medium ${
                          product.stock > 10 ? 'text-green-600' :
                          product.stock > 0 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-1">
                          <Star size={14} className="fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{product.rating || '0.0'}</span>
                          <span className="text-xs text-gray-400">
                            ({product.reviews_count || 0})
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-medium">{product.sold_count || 0}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/product/${product.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="查看"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                            title="編輯"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(product)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="刪除"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 刪除確認對話框 */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-red-50 rounded-full">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  確認刪除商品
                </h3>
                <p className="text-gray-600">
                  您確定要刪除「{deleteConfirm.name}」嗎？此操作無法復原。
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSellerProducts;